import { useState, useEffect } from 'react'
import { Play, CheckCircle, AlertCircle, Clock, BarChart3, Search, ArrowRight, Shield, ShieldCheck, ShieldAlert, Zap, Activity, Database, FileCheck } from 'lucide-react'
import { Link } from 'react-router-dom'

// Mapping internal audit types to professional display names
const auditTypes = [
  {
    id: 'security',
    name: 'Security Vulnerability Audit',
    icon: <Shield className="text-red-400" size={32} />,
    description: 'Comprehensive security assessment of environment variables, API keys, and network exposure.',
    duration: '1-2 min',
    difficulty: 'Advanced',
  },
  {
    id: 'performance',
    name: 'System Performance Audit',
    icon: <Zap className="text-yellow-400" size={32} />,
    description: 'Analyze database latency, storage I/O, and system response times.',
    duration: '1 min',
    difficulty: 'Intermediate',
  },
  {
    id: 'compliance',
    name: 'Regulatory Compliance Audit',
    icon: <FileCheck className="text-blue-400" size={32} />,
    description: 'Check alignment with ISO 27001 and GDPR documentation requirements.',
    duration: '2-3 min',
    difficulty: 'Advanced',
  },
  {
    id: 'access_control',
    name: 'IAM & Access Audit',
    icon: <Activity className="text-purple-400" size={32} />,
    description: 'Review user permissions, administrative roles, and authentication logs.',
    duration: '2 min',
    difficulty: 'Intermediate',
  },
]

export default function LiveAuditPage() {
  const [selectedAudit, setSelectedAudit] = useState(null)
  const [isRunning, setIsRunning] = useState(false)
  const [progress, setProgress] = useState(0)
  const [auditId, setAuditId] = useState(null)
  const [auditResults, setAuditResults] = useState(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState(null)

  // Hardcoded company ID for demonstration - in production this comes from auth context
  const COMPANY_ID = '00000000-0000-0000-0000-000000000001' 

  // Polling logic when an audit is running
  useEffect(() => {
    let interval
    if (isRunning && auditId) {
      interval = setInterval(async () => {
        try {
          const response = await fetch(`http://localhost:8000/api/auditor/details/${auditId}`)
          if (response.ok) {
            const data = await response.json()
            if (data.record.status !== 'in_progress') {
              setIsRunning(false)
              setProgress(100)
              setAuditResults(data)
              clearInterval(interval)
            } else {
              // Simulate progress since backend background tasks don't report %
              setProgress(prev => Math.min(prev + 10, 95))
            }
          }
        } catch (err) {
          console.error("Polling error:", err)
        }
      }, 2000)
    }
    return () => clearInterval(interval)
  }, [isRunning, auditId])

  const handleStartAudit = async () => {
    if (!selectedAudit) return

    setError(null)
    setIsRunning(true)
    setProgress(0)
    setAuditResults(null)

    try {
      const response = await fetch('http://localhost:8000/api/auditor/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audit_type: selectedAudit.id,
          company_id: COMPANY_ID
        })
      })

      if (!response.ok) throw new Error('Failed to start audit')
      
      const data = await response.json()
      setAuditId(data.audit_id)
      setProgress(10)
    } catch (err) {
      setError(err.message)
      setIsRunning(false)
    }
  }

  const getSeverityColor = (severity) => {
    switch (severity.toLowerCase()) {
      case 'critical': return 'text-red-500 bg-red-500/10 border-red-500/20'
      case 'high': return 'text-orange-500 bg-orange-500/10 border-orange-500/20'
      case 'medium': return 'text-yellow-500 bg-yellow-500/10 border-yellow-500/20'
      default: return 'text-blue-500 bg-blue-500/10 border-blue-500/20'
    }
  }

  const getStatusIcon = (status) => {
    if (status === 'passed') return <ShieldCheck className="text-green-400" size={48} />
    if (status === 'failed') return <ShieldAlert className="text-red-400" size={48} />
    return <Activity className="text-yellow-400" size={48} />
  }

  const filteredAudits = auditTypes.filter(
    (audit) =>
      audit.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      audit.description.toLowerCase().includes(searchTerm.toLowerCase())
  )

  if (auditResults) {
    const { record, findings } = auditResults
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white">Audit Report</h1>
            <p className="text-blue-200 mt-1">Generated for {record.audit_type.toUpperCase()} assessment</p>
          </div>
          <button
            onClick={() => {
              setAuditResults(null)
              setSelectedAudit(null)
              setAuditId(null)
            }}
            className="px-4 py-2 bg-slate-800 text-blue-300 hover:bg-slate-700 rounded-lg border border-blue-500/30 transition-colors"
          >
            Start New Audit
          </button>
        </div>

        {/* Results Summary Header */}
        <div className={`bg-gradient-to-br from-slate-900 to-slate-800 rounded-xl border ${record.status === 'passed' ? 'border-green-500/30' : 'border-red-500/30'} p-8 shadow-2xl`}>
          <div className="flex flex-col md:flex-row items-center md:items-start space-y-6 md:space-y-0 md:space-x-8">
            <div className="flex-shrink-0 bg-slate-900/50 p-6 rounded-2xl border border-blue-500/10 shadow-inner">
              {getStatusIcon(record.status)}
            </div>
            
            <div className="flex-grow grid grid-cols-2 lg:grid-cols-4 gap-8">
              <div className="space-y-1">
                <p className="text-blue-300/50 text-xs font-bold uppercase tracking-wider">Overall Posture</p>
                <p className={`text-2xl font-black ${record.status === 'passed' ? 'text-green-400' : 'text-red-400'}`}>
                  {record.status.toUpperCase()}
                </p>
              </div>
              <div className="space-y-1">
                <p className="text-blue-300/50 text-xs font-bold uppercase tracking-wider">Completed At</p>
                <p className="text-lg font-bold text-white">{new Date(record.completed_at).toLocaleTimeString()}</p>
              </div>
              <div className="space-y-1">
                <p className="text-blue-300/50 text-xs font-bold uppercase tracking-wider">Audit Reference</p>
                <p className="text-xs font-mono text-blue-200 truncate">{record.id}</p>
              </div>
              <div className="space-y-1 text-right">
                <p className="text-blue-300/50 text-xs font-bold uppercase tracking-wider">Findings</p>
                <p className="text-2xl font-black text-white">{record.total_findings}</p>
              </div>
            </div>
          </div>

          {/* Risk Triage Bar */}
          <div className="grid grid-cols-4 gap-2 mt-8">
            <div className="h-2 bg-red-500/20 rounded-full overflow-hidden">
               <div className="h-full bg-red-500 transition-all duration-1000" style={{ width: `${(record.critical_issues / record.total_findings) * 100}%` }}></div>
            </div>
            <div className="h-2 bg-orange-500/20 rounded-full overflow-hidden">
               <div className="h-full bg-orange-500 transition-all duration-1000" style={{ width: `${(record.high_risk_issues / record.total_findings) * 100}%` }}></div>
            </div>
            <div className="h-2 bg-yellow-500/20 rounded-full overflow-hidden">
               <div className="h-full bg-yellow-500 transition-all duration-1000" style={{ width: `${(record.medium_risk_issues / record.total_findings) * 100}%` }}></div>
            </div>
            <div className="h-2 bg-blue-500/20 rounded-full overflow-hidden">
               <div className="h-full bg-blue-500 transition-all duration-1000" style={{ width: `${(record.low_risk_issues / record.total_findings) * 100}%` }}></div>
            </div>
          </div>
          <div className="flex justify-between mt-2 text-[10px] font-bold text-blue-300/40 uppercase tracking-widest px-1">
            <span>Critical</span>
            <span>High Risk</span>
            <span>Medium</span>
            <span>Information</span>
          </div>
        </div>

        {/* Detailed Findings List */}
        <div className="space-y-4">
          <h2 className="text-xl font-bold text-white flex items-center space-x-2">
            <Activity size={20} className="text-blue-400" />
            <span>Detailed Analysis Findings</span>
          </h2>
          
          <div className="grid gap-4">
            {findings.map((item, idx) => (
              <div key={idx} className="group bg-slate-800/40 hover:bg-slate-800/60 rounded-xl border border-blue-500/10 p-5 transition-all hover:border-blue-500/30">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-grow space-y-2">
                    <div className="flex items-center space-x-3">
                      <span className={`text-[10px] font-black px-2 py-0.5 rounded border uppercase ${getSeverityColor(item.severity)}`}>
                        {item.severity}
                      </span>
                      <h3 className="text-lg font-bold text-white group-hover:text-blue-300 transition-colors">{item.title}</h3>
                    </div>
                    <p className="text-sm text-blue-200/80 leading-relaxed">{item.description}</p>
                    
                    {/* Remediation Box */}
                    {item.remediation && (
                      <div className="mt-4 bg-blue-500/5 rounded-lg p-3 border border-blue-500/10 flex items-start space-x-3">
                        <AlertCircle className="text-blue-400 flex-shrink-0 mt-0.5" size={16} />
                        <div>
                          <p className="text-[11px] font-bold text-blue-400 uppercase tracking-tighter">Remediation Action</p>
                          <p className="text-sm text-blue-100/90">{item.remediation}</p>
                        </div>
                      </div>
                    )}
                  </div>
                  
                  <div className="flex-shrink-0 flex md:flex-col items-center justify-between md:justify-start gap-4">
                    <div className={`px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest ${
                      item.status === 'pass' ? 'bg-green-500/20 text-green-400 border border-green-500/30' :
                      item.status === 'warning' ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30' :
                      'bg-red-500/20 text-red-400 border border-red-500/30'
                    }`}>
                      {item.status}
                    </div>
                    <button className="text-blue-400 hover:text-white text-[10px] font-bold uppercase tracking-wider flex items-center space-x-1 transition-colors">
                      <span>View Details</span>
                      <ArrowRight size={12} />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Professional Export Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 pt-6">
          <button className="px-6 py-4 bg-gradient-to-br from-blue-600 to-indigo-700 text-white rounded-xl font-black text-sm uppercase tracking-widest hover:shadow-[0_0_20px_rgba(37,99,235,0.4)] transition-all flex items-center justify-center space-x-3">
             <FileCheck size={20} />
             <span>Export PDF Report</span>
          </button>
          <button className="px-6 py-4 bg-slate-800 text-blue-200 rounded-xl font-black text-sm uppercase tracking-widest border border-blue-500/20 hover:bg-slate-700 transition-all flex items-center justify-center space-x-3">
             <Database size={20} />
             <span>Export Raw JSON</span>
          </button>
          <button className="px-6 py-4 bg-green-600/20 text-green-400 rounded-xl font-black text-sm uppercase tracking-widest border border-green-500/30 hover:bg-green-600/30 transition-all flex items-center justify-center space-x-3">
             <ShieldCheck size={20} />
             <span>Acknowledge All</span>
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8 pb-20">
      <div className="space-y-2">
        <div className="flex items-center space-x-3">
          <div className="p-3 bg-blue-600/20 rounded-lg border border-blue-500/30">
            <Shield className="text-blue-400" size={24} />
          </div>
          <h1 className="text-4xl font-black text-white tracking-tight">Cybertection <span className="text-blue-500">Auditor</span></h1>
        </div>
        <p className="text-blue-200/60 text-lg max-w-2xl">
          Execute automated security, compliance, and performance scans against your entire ecosystem. 
          Professional-grade assessment for mission-critical infrastructure.
        </p>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 p-4 rounded-xl flex items-center space-x-3 text-red-400">
          <ShieldAlert size={20} />
          <p className="font-bold">{error}</p>
        </div>
      )}

      {selectedAudit ? (
        <div className="animate-in slide-in-from-bottom-4 duration-500">
          {/* Selected Audit Focus View */}
          <div className="bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-blue-500/20 p-10 shadow-2xl relative overflow-hidden">
            <div className="absolute top-0 right-0 p-20 opacity-5 pointer-events-none">
                {selectedAudit.icon}
            </div>
            
            <div className="relative z-10 flex flex-col md:flex-row md:items-center justify-between gap-8">
              <div className="space-y-6 flex-grow">
                <button 
                  onClick={() => setSelectedAudit(null)}
                  className="text-xs font-black text-blue-400 uppercase tracking-widest hover:text-white transition-colors flex items-center space-x-2"
                >
                   <ArrowRight className="rotate-180" size={14} />
                   <span>Change Assessment</span>
                </button>
                
                <div className="space-y-4">
                  <h2 className="text-4xl font-black text-white">{selectedAudit.name}</h2>
                  <p className="text-blue-100 text-lg max-w-xl leading-relaxed">{selectedAudit.description}</p>
                </div>

                <div className="flex items-center gap-8">
                  <div className="flex items-center space-x-2">
                    <Clock className="text-blue-400" size={18} />
                    <span className="text-sm font-bold text-white uppercase tracking-tighter">Est. Time: {selectedAudit.duration}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <BarChart3 className="text-blue-400" size={18} />
                    <span className="text-sm font-bold text-white uppercase tracking-tighter">Complexity: {selectedAudit.difficulty}</span>
                  </div>
                </div>
              </div>

              <div className="flex-shrink-0 w-full md:w-auto">
                {!isRunning ? (
                  <button
                    onClick={handleStartAudit}
                    className="w-full md:w-auto px-10 py-5 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-2xl font-black text-lg uppercase tracking-widest hover:shadow-[0_0_30px_rgba(37,99,235,0.5)] transition-all flex items-center justify-center space-x-3 group"
                  >
                    <Play className="group-hover:scale-125 transition-transform" size={24} fill="currentColor" />
                    <span>Initialize Scan</span>
                  </button>
                ) : (
                   <div className="text-center space-y-4">
                      <div className="relative w-24 h-24 mx-auto">
                        <svg className="w-24 h-24 rotate-[-90deg]">
                          <circle className="text-slate-700" strokeWidth="6" stroke="currentColor" fill="transparent" r="40" cx="48" cy="48" />
                          <circle className="text-blue-500" strokeWidth="6" strokeDasharray={251.2} strokeDashoffset={251.2 - (251.2 * progress) / 100} strokeLinecap="round" stroke="currentColor" fill="transparent" r="40" cx="48" cy="48" />
                        </svg>
                        <div className="absolute inset-0 flex items-center justify-center text-sm font-black text-white">
                          {Math.round(progress)}%
                        </div>
                      </div>
                      <p className="text-xs font-black text-blue-400 uppercase animate-pulse tracking-widest">Assessment Running...</p>
                   </div>
                )}
              </div>
            </div>
          </div>
        </div>
      ) : (
        /* Professional Selection Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredAudits.map((audit) => (
            <div
              key={audit.id}
              onClick={() => setSelectedAudit(audit)}
              className="group bg-slate-900/50 hover:bg-slate-800/80 rounded-2xl border border-blue-500/10 p-8 cursor-pointer transition-all hover:border-blue-500/40 hover:-translate-y-1 shadow-xl"
            >
              <div className="flex items-start justify-between mb-8">
                <div className="p-4 bg-slate-900 rounded-2xl border border-blue-500/10 group-hover:border-blue-500/30 transition-all shadow-inner">
                  {audit.icon}
                </div>
                <span className="text-[10px] font-black text-blue-500 uppercase tracking-[0.2em] opacity-40 group-hover:opacity-100 transition-opacity">
                  ID: {audit.id}
                </span>
              </div>
              
              <div className="space-y-4">
                <h3 className="text-2xl font-black text-white group-hover:text-blue-400 transition-colors">{audit.name}</h3>
                <p className="text-blue-200/60 text-sm leading-relaxed line-clamp-2">{audit.description}</p>
              </div>

              <div className="mt-8 pt-6 border-t border-blue-500/5 flex items-center justify-between">
                <div className="flex gap-4">
                   <div className="flex items-center space-x-1.5 text-blue-400/50 group-hover:text-blue-400 transition-colors">
                      <Clock size={14} />
                      <span className="text-[10px] font-bold uppercase tracking-tighter">{audit.duration}</span>
                   </div>
                   <div className="flex items-center space-x-1.5 text-blue-400/50 group-hover:text-blue-400 transition-colors">
                      <BarChart3 size={14} />
                      <span className="text-[10px] font-bold uppercase tracking-tighter">{audit.difficulty}</span>
                   </div>
                </div>
                <div className="w-8 h-8 rounded-full bg-blue-500/10 flex items-center justify-center text-blue-400 group-hover:bg-blue-500 group-hover:text-white transition-all">
                   <ArrowRight size={18} />
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Footer Info */}
      <div className="bg-blue-600/5 border border-blue-500/10 rounded-2xl p-6 flex flex-col md:flex-row items-center justify-between gap-6">
        <div className="flex items-center space-x-4">
          <Activity className="text-blue-500" size={24} />
          <div>
            <p className="text-sm font-black text-white uppercase tracking-wider">Operational Integrity</p>
            <p className="text-xs text-blue-300/60">All scans are performed in a non-destructive read-only mode.</p>
          </div>
        </div>
        <div className="flex gap-3">
          <Link to="/audit-history" className="px-4 py-2 text-xs font-black text-blue-400 uppercase tracking-widest hover:text-white transition-colors">
            View History
          </Link>
          <button className="px-4 py-2 bg-blue-500/10 text-blue-400 rounded-lg text-xs font-black uppercase tracking-widest hover:bg-blue-500/20 transition-all">
            Audit Settings
          </button>
        </div>
      </div>
    </div>
  )
}
